import { useEffect, useState } from "react"
import { formatCurrencyWithNumberToSymbolWithNumber as money } from "@ui/utils/formatter"
import { systemErrorDetails, systemPost } from "src/services/superAdminApi"
import { T } from "../adminTheme"
import { Fact, Facts, Fields, Muted } from "./adminUi"

// Support calculator for system/crypto/quote (Batch_2026-10-26_FE.md §A): how much crypto to send so the
// wanted amount lands on the company balance. Every figure comes from the API; nothing is summed here.

interface Quote {
	coin_name: string
	send_display: string
	credit_cents: number
	forwarded_cents: number
	issuer_fee_cents: number
	issuer_fee_percent: number
	issuer_fee_source?: "observed" | "config"
	percent_cents: number
	fee_percent: number
	gas_fee_cents: number
	gas_transfers: number
	fixed_cents: number
	min_deposit_cents: number
	stale: boolean
}

const CHAIN_PAUSED = 2712
const DEBOUNCE_MS = 400
const DEFAULT_COIN = "USDT_TRC20"

const usd = (cents: number) => money(cents, "USD")
/** Percents arrive in hundredths (30 = 0.30%). */
const percent = (value: number) => `${(value / 100).toFixed(2)}%`

export const CryptoQuote = ({ token, companyUid, corporate, coinKeys = [] }: { token: string; companyUid: string; corporate: boolean; coinKeys?: string[] }) => {
	const [amount, setAmount] = useState("100")
	const [coin, setCoin] = useState(DEFAULT_COIN)
	const [state, setState] = useState<{ quote?: Quote; error?: string; loading?: boolean }>({})

	useEffect(() => {
		const cents = Math.round(Number(amount) * 100)
		if (!amount.trim() || !Number.isFinite(cents) || cents <= 0) return setState({})
		let active = true
		setState((previous) => ({ ...previous, loading: true }))
		// Gas is a live flat fee, so every change of amount or network is re-quoted; never cached.
		const timer = setTimeout(async () => {
			const response = await systemPost<Quote>(token, "system/crypto/quote", { company_uid: companyUid, amount: cents, coin_key: coin.trim() || undefined })
			if (!active) return
			if (response.error) setState({ error: Number(response.error.code) === CHAIN_PAUSED ? `Network unavailable: ${systemErrorDetails(response.error)}` : systemErrorDetails(response.error) })
			else setState({ quote: response.data })
		}, DEBOUNCE_MS)
		return () => { active = false; clearTimeout(timer) }
	}, [token, companyUid, amount, coin])

	const { quote, error, loading } = state
	return <section>
		<div style={{ marginBottom: 12 }}><h3>Crypto top-up calculator</h3><Muted style={{ fontSize: 13 }}>How much to send so the amount lands on this balance, and how much of it the issuer takes.</Muted></div>
		<Fields>
			<label>Amount to balance, $<input type='number' min='1' step='0.01' inputMode='decimal' value={amount} onChange={(event) => setAmount(event.target.value)} aria-invalid={Boolean(error)} /></label>
			<label>Network<input list='crypto-quote-coins' value={coin} onChange={(event) => setCoin(event.target.value)} placeholder={DEFAULT_COIN} /></label>
			<datalist id='crypto-quote-coins'>{[...new Set([DEFAULT_COIN, ...coinKeys])].map((key) => <option key={key} value={key} />)}</datalist>
		</Fields>
		{error && <div role='alert' style={{ marginTop: 8, color: T.danger, fontSize: 13 }}>{error}</div>}
		{quote && !error && <div style={{ marginTop: 16, opacity: loading ? .55 : 1 }}>
			{quote.stale && <div role='status' style={{ marginBottom: 10, color: T.warn, fontSize: 13 }}>May be inaccurate: the rate or gas could not be refreshed. Recalculate.</div>}
			<Facts>
				<Fact label='Send'><span style={{ fontSize: 17, fontWeight: 650 }}>{quote.send_display} {quote.coin_name}</span></Fact>
				<Fact label='Lands on balance'>{usd(quote.credit_cents)}</Fact>
				<Fact label='Goes to issuer'>{usd(quote.forwarded_cents)}</Fact>
				<Fact label='Issuer fee'>{usd(quote.issuer_fee_cents)} ({percent(quote.issuer_fee_percent)}){quote.issuer_fee_source && <Muted> · {quote.issuer_fee_source === "observed" ? "observed" : "default"}</Muted>}</Fact>
				<Fact label='Our fee (tariff)'>{usd(quote.percent_cents)} ({percent(quote.fee_percent)})</Fact>
				<Fact label='Network gas'>{usd(quote.gas_fee_cents)}<Muted> · {quote.gas_transfers} transfer{quote.gas_transfers === 1 ? "" : "s"}</Muted></Fact>
				<Fact label='Fixed part'>{usd(quote.fixed_cents)}</Fact>
				<Fact label='Min. deposit on this network'>{usd(quote.min_deposit_cents)}</Fact>
			</Facts>
			{corporate && <Muted style={{ display: "block", marginTop: 10, fontSize: 13 }}>Processing per the company tariff is charged after crediting.</Muted>}
		</div>}
	</section>
}
